import * as React from "react";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkLoader } from "uitk-react-loader";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkHeading, UitkHeadingProps } from "uitk-react-text";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { UitkSpacing } from "uitk-react-spacing";
import { MerchCampaignsFlexModuleResult } from "typings/microserviceModels/merch-campaigns-flex-module";
import { CampaignHeader } from "../../shared/Merchandising/Common/CampaignHeader/CampaignHeader";
import { locKeys } from "./l10n";
import { CampaignPlaceholder } from "../../shared/Merchandising/Common/CampaignPlaceholder/CampaignPlaceholder";
import {
  CarouselModuleViewTypes,
  MerchCampaignCard,
} from "../../shared/Merchandising/Common/MerchCampaignCard/MerchCampaignCard";
import { CampaignRecommendationsWithOffersQuery } from "../../../../__generated__/api";
import { MerchCampaignProps } from "./typings";
import Campaigns = CampaignRecommendationsWithOffersQuery.Campaigns;

const MerchCampaign = withStores(
  "routeStore",
  "merchCampaigns",
  "flexViewModel",
  "flexModuleModelStore"
)(
  observer((props: MerchCampaignProps) => {
    const { flexTemplateNode, flexModuleModelStore, merchCampaigns, context, flexViewModel, routeStore } = props;
    const { id, options } = flexTemplateNode;
    const { fmId } = options;
    const model = flexModuleModelStore.getModel(id) as MerchCampaignsFlexModuleResult | null;
    const keyHelper = new ItemKeyHelper(id);
    const { lat, long, origin, referrer } = props.routeStore.queryParams;
    const userLatitude = lat || flexViewModel?.userLocation?.latitude;
    const userLongitude = long || flexViewModel?.userLocation?.longitude;
    const { userTripState } = props.routeStore.queryParams;
    const pageName = flexViewModel.trackingContext?.pageName;
    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const { pathname, search } = props.routeStore;
    const returnPath = `${pathname}${search}`;

    if (!model) {
      return null;
    }

    const {
      numberOfCampaigns,
      destinationView,
      displayDate,
      displayDescription,
      displayPrice,
      paginationEnabled,
      filterByProductType,
      paginationMaxCampaigns,
      title,
      filterByRecommendationType,
      enableLivePricing,
    } = model;

    const fetchCampaigns = async () => {
      showMoreButtonRef.current?.blur();
      await merchCampaigns.fetchRecommendedCampaigns(
        context,
        numberOfCampaigns,
        paginationEnabled ? paginationMaxCampaigns : null,
        filterByProductType,
        filterByRecommendationType,
        userLatitude,
        userLongitude,
        origin,
        pageName,
        userTripState,
        referrer,
        enableLivePricing,
        returnPath
      );
    };

    React.useEffect(() => {
      if (merchCampaigns?.campaigns?.length === 0) {
        fetchCampaigns();
      }
    }, []);

    const openDialog = (dialogPath: string) => {
      routeStore.updateSearch({ pwaDialog: dialogPath });
    };

    const closeDialog = () => {
      routeStore.closeDialog("pwaDialog");
    };

    const renderOffers = (campaign: Campaigns) =>
      campaign.offers?.length && campaign.offers?.length >= 4 && campaign.productTypes.length > 0;

    const { formatText } = useLocalization();
    const titleConfig: UitkHeadingProps = {
      level: 1,
      size: 800,
    };
    const track = useClickTracker();

    const defaultUndefinedVisibleItems = CarouselModuleViewTypes.DEFAULT;

    const VisibleItemsMap = {};
    VisibleItemsMap[CarouselModuleViewTypes.VR] = {
      lg: 2,
      md: 2,
      sm: 2,
    };
    VisibleItemsMap[CarouselModuleViewTypes.REDESIGN] = {
      lg: 4,
      md: 4,
      sm: 2,
    };
    VisibleItemsMap[CarouselModuleViewTypes.DEFAULT] = {
      lg: 3,
      md: 2,
      sm: 1,
    };

    const getVisibleItemsForModelView = (modelView: string) =>
      VisibleItemsMap[modelView ?? defaultUndefinedVisibleItems];

    const numberOfVisibleItems = getVisibleItemsForModelView(model.view);

    const renderCarousel = (campaign: Campaigns, visibleItem: any) => (
      <UitkSpacing margin={{ blockstart: "three" }}>
        <IsomorphicCarousel
          className="carousel-component"
          itemsVisible={visibleItem}
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
          pageBy={1}
          peekWidth={0.75}
        >
          {campaign.offers?.map((offer, cardIndex) => (
            <div className="uitk-height-item" key={keyHelper.next()}>
              <MerchCampaignCard
                offer={offer}
                cardIndex={cardIndex}
                campaignId={campaign.id}
                productTypes={campaign.productTypes}
                recommendedType={campaign.recommendedType}
                openDialog={openDialog}
                closeDialog={closeDialog}
                destinationView={destinationView}
                displayDate={displayDate}
                displayDescription={displayDescription}
                displayPrice={displayPrice}
                border
                moduleName="MerchCampaign"
              />
            </div>
          )) || []}
        </IsomorphicCarousel>
      </UitkSpacing>
    );

    const callback = (moduleId: string) => {
      const flexTracking: FlexTrackingInfo = {
        moduleName: moduleId,
        rfrr: `${flexViewModel.trackingContext?.pageName}.pagination`,
        action: Action.CLICK,
      };
      sendDelayedClickstreamTrackEvent(flexTracking, track);
    };

    if (merchCampaigns.campaigns.length === 0) {
      return merchCampaigns.isLoading ? (
        <CampaignPlaceholder numberOfCampaigns={numberOfCampaigns} formatText={formatText} />
      ) : null;
    }

    return (
      <UitkSpacing margin={{ blockend: "three" }}>
        <div data-testid="merch-campaign" className="MerchCampaign" id={id} data-fm={fmId}>
          {title && (
            <UitkSpacing margin={{ blockstart: "four" }} padding={{ blockstart: "four" }}>
              <div className="uitk-type-center">
                <UitkHeading {...titleConfig}>{title}</UitkHeading>
              </div>
            </UitkSpacing>
          )}

          {merchCampaigns.campaigns?.filter(renderOffers).map((campaign: Campaigns, campaignIndex: number) => (
            <>
              <UitkSpacing padding={{ block: "six" }}>
                <div data-testid="merch-campaign-carousel" key={keyHelper.next()}>
                  <CampaignHeader campaign={campaign} openDialog={openDialog} closeDialog={closeDialog} />

                  <Viewport>
                    <ViewSmall>{renderCarousel(campaign, numberOfVisibleItems)}</ViewSmall>
                    {model.view === CarouselModuleViewTypes.VR ? (
                      <ViewMedium>
                        <UitkSpacing padding={{ inline: "twelve" }}>
                          <UitkLayoutGrid columns={4} space="three">
                            {campaign.offers?.slice(0, 4).map((offer, cardIndex) => (
                              <div className="uitk-height-item" key={keyHelper.next()} data-testid="merch-grid">
                                <MerchCampaignCard
                                  offer={offer}
                                  cardIndex={cardIndex}
                                  campaignId={campaign.id}
                                  productTypes={campaign.productTypes}
                                  recommendedType={campaign.recommendedType}
                                  openDialog={openDialog}
                                  closeDialog={closeDialog}
                                  destinationView={destinationView}
                                  displayDate={displayDate}
                                  displayDescription={displayDescription}
                                  displayPrice={displayPrice}
                                  border
                                  moduleName="MerchCampaign"
                                />
                              </div>
                            )) || []}
                          </UitkLayoutGrid>
                        </UitkSpacing>
                      </ViewMedium>
                    ) : (
                      <ViewMedium>{renderCarousel(campaign, numberOfVisibleItems)}</ViewMedium>
                    )}
                  </Viewport>
                </div>
              </UitkSpacing>
            </>
          ))}

          {paginationEnabled &&
            merchCampaigns.hasNextPage &&
            !(merchCampaigns.campaigns.length === paginationMaxCampaigns) && (
              <UitkSpacing padding={{ blockend: "six" }}>
                <div className="uitk-type-center">
                  <UitkSecondaryButton
                    name="showMoreButton"
                    type="button"
                    onClick={fetchCampaigns}
                    text={formatText(locKeys.showMore)}
                    domRef={showMoreButtonRef}
                    analytics={{
                      id: "merch.campaign",
                      callback,
                    }}
                  />
                </div>
              </UitkSpacing>
            )}
          {merchCampaigns.isLoading && (
            <UitkLayoutFlex justifyContent="center">
              <UitkSpacing margin={{ blockstart: "four" }}>
                <UitkLayoutFlexItem>
                  <div>
                    <UitkLoader />
                  </div>
                </UitkLayoutFlexItem>
              </UitkSpacing>
            </UitkLayoutFlex>
          )}
        </div>
      </UitkSpacing>
    );
  })
);
export default MerchCampaign;
